import { NgIf } from '@angular/common';
import {
	Component,
	EventEmitter,
	HostBinding,
	Input,
	Output
} from '@angular/core';
import { ReportBlockRender } from '../../../../services/report-blocks.service';
import { ReportSectionChartSwitch } from '../../../../services/report-chart-types';
import { ReportChartDrawingComponent } from '../../widgets/chart-drawing/chart-drawing.component';
import { ReportChartTypeToolbarComponent } from '../report-chart-type-toolbar/report-chart-type-toolbar.component';

/**
 * The switchable part of a fixed section's chart, laid over the chart's box.
 *
 * A fixed section draws its chart in its own markup, matched to the report
 * design, and none of that markup is touched: while the section is drawn as
 * designed this component is an empty, out-of-flow box in the chart area's
 * corner holding the type menu (editor only). Once another type is picked the
 * slide hides its own drawing and this box grows to cover the chart area —
 * inside its padding, so the card around it is unchanged — and draws the same
 * numbers as the chosen type. Only the chart part of the slide changes.
 *
 * Covering rather than replacing is deliberate. The sixteen slides lay their
 * charts out sixteen different ways — a canvas in a flex column, a donut in an
 * absolutely-filled square, rows of HTML bars — and a wrapper that sat between
 * any of them and its parent would have had to re-create each layout to leave
 * the default drawing exactly as it was.
 *
 * The chart area it sits in carries `slide__chart-host` (see _slide-base),
 * which positions it and shows the menu on hover or keyboard focus.
 */
@Component({
	standalone: true,
	selector: 'app-report-chart-switch',
	imports: [
		NgIf,
		ReportChartDrawingComponent,
		ReportChartTypeToolbarComponent
	],
	template: `
		<app-report-chart-drawing
			*ngIf="state?.block"
			[block]="state!.block"
			[themeColor]="themeColor"
			[fill]="true"
		></app-report-chart-drawing>

		<app-report-chart-type-toolbar
			*ngIf="editorMode && state && state.types.length > 1"
			class="chart-switch__types"
			[types]="state.types"
			[current]="state.current"
			[advice]="state.advice"
			(picked)="onPicked($event)"
		></app-report-chart-type-toolbar>
	`,
	styles: [
		`
			:host {
				position: absolute;
				top: 0;
				right: 0;
				z-index: 2;
			}

			:host(.chart-switch--switched) {
				inset: 0;
				/* The chart area's own padding, so a card keeps its inset and
				   a bare chart box its full size. */
				padding: inherit;
				box-sizing: border-box;
				display: flex;
				flex-direction: column;
			}

			:host(.chart-switch--switched) app-report-chart-drawing {
				flex: 1;
				min-height: 0;
			}

			.chart-switch__types {
				position: absolute;
				top: 8px;
				right: 8px;
				/* The slide is scaled down to fit the preview; the menu is not
				   part of the slide, so it is scaled back up to stay a
				   clickable size. The export sets no scale, and shows no menu. */
				transform: scale(calc(1 / var(--sl-report-preview-scale, 1)));
				transform-origin: top right;
			}
		`
	]
})
export class ReportChartSwitchComponent {
	/** What the section's chart is drawn as now, and what it may become. */
	@Input() state: ReportSectionChartSwitch | null = null;
	@Input() themeColor: string | null = null;
	@Input() editorMode = false;

	/** The type picked; null when it is the section's own design again. */
	@Output() chartTypeChange = new EventEmitter<ReportBlockRender | null>();

	@HostBinding('class.chart-switch--switched')
	get switched(): boolean {
		return Boolean(this.state?.block);
	}

	onPicked(render: ReportBlockRender): void {
		// The section's own design is stored as no choice at all, so a report
		// switched back reads exactly like one never switched.
		this.chartTypeChange.emit(
			render === this.state?.designedAs ? null : render
		);
	}
}
